import { Check, Plus } from 'lucide-react';
import styled from 'styled-components';

import type { GetOrderCatalogueQuery } from 'data/__generated__';

import { formatEuroAmount } from 'helpers/price';
import { PRODUCT_FLOW_LABELS } from 'pages/Products/constants/flows';

export type CatalogueProduct = GetOrderCatalogueQuery['orderCatalogue']['products'][number];

export const CatalogueGrid = ({
    products,
    quantityByProductId,
    onAdd,
}: {
    products: CatalogueProduct[];
    quantityByProductId: Record<string, number>;
    onAdd: (product: CatalogueProduct) => void;
}) => {
    if (!products.length) {
        return <EmptyState>Aucun produit ne correspond à cette recherche.</EmptyState>;
    }

    return (
        <Grid>
            {products.map((product) => {
                const quantity = quantityByProductId[product._id] ?? 0;
                const isOutOfStock = product.availableStock != null && product.availableStock <= 0;

                return (
                    <Card key={product._id} data-test={`catalogue-product-${product._id}`}>
                        <Thumbnail>
                            {product.imageUrl ? <img src={product.imageUrl} alt="" /> : <NoImage>—</NoImage>}
                        </Thumbnail>
                        <CardBody>
                            <Name title={product.name}>{product.name}</Name>
                            <Meta>
                                {[product.brand, product.volume, PRODUCT_FLOW_LABELS[product.flow]]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </Meta>
                            <Price>
                                {formatEuroAmount(product.unitPrice)}
                                <Step> · par {getStep(product)}</Step>
                            </Price>
                            <Tags>
                                {isOutOfStock ? <Tag $tone="warning">Stock épuisé</Tag> : null}
                                {product.availableStock != null && product.availableStock > 0 ? (
                                    <Tag $tone="muted">{product.availableStock} dispo</Tag>
                                ) : null}
                                {!product.isDeliverableOnDeliveryDate ? (
                                    <Tag $tone="warning">Non livrable ce jour</Tag>
                                ) : null}
                            </Tags>
                        </CardBody>
                        <AddButton
                            type="button"
                            $isInCart={quantity > 0}
                            onClick={() => onAdd(product)}
                            title={quantity > 0 ? 'Ajouter encore' : 'Ajouter au panier'}
                            data-test={`catalogue-add-${product._id}`}
                        >
                            {quantity > 0 ? (
                                <>
                                    <Check size={14} /> {quantity}
                                </>
                            ) : (
                                <Plus size={14} />
                            )}
                        </AddButton>
                    </Card>
                );
            })}
        </Grid>
    );
};

/** Le pas de saisie : une unité commandée vaut `conditionningTotem / portion` produits. */
export function getStep(product: Pick<CatalogueProduct, 'conditionningTotem' | 'portion'>): number {
    return Math.max(Math.round(product.conditionningTotem / product.portion), 1);
}

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 10px;
`;

const EmptyState = styled.div`
    color: ${({ theme }) => theme.infoTextColor};
    padding: 20px;
`;

const Card = styled.div`
    display: grid;
    grid-template-columns: 48px 1fr auto;
    align-items: center;
    gap: 10px;
    padding: 8px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;

const Thumbnail = styled.div`
    width: 48px;
    height: 48px;
    border-radius: ${({ theme }) => theme.borderRadius};
    background-color: ${({ theme }) => theme.pictureBackgroundColor};
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const NoImage = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: ${({ theme }) => theme.infoTextColor};
`;

const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const Name = styled.span`
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Meta = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 12px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Price = styled.span`
    font-size: 13px;
`;

const Step = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
`;

const Tags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 2px;
`;

const Tag = styled.span<{ $tone: 'warning' | 'muted' }>`
    font-size: 11px;
    padding: 0 4px;
    border-radius: 3px;
    color: ${({ theme, $tone }) => ($tone === 'warning' ? theme.warningColor : theme.infoTextColor)};
    border: 1px solid currentColor;
`;

const AddButton = styled.button<{ $isInCart: boolean }>`
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 32px;
    height: 28px;
    padding: 0 8px;
    justify-content: center;
    border: 1px solid ${({ theme }) => theme.ctaPrimaryBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    cursor: pointer;
    background-color: ${({ theme, $isInCart }) => ($isInCart ? theme.ctaPrimaryColor : 'transparent')};
    color: ${({ theme, $isInCart }) => ($isInCart ? theme.ctaPrimaryTextColor : theme.textColor)};
`;
